import { OverlayContainer } from '@angular/cdk/overlay';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { DestroyRef, PLATFORM_ID, computed, inject, signal, Service } from '@angular/core';

export type ThemePreference = 'light' | 'dark';

const DARK_CLASS = 'fb-theme-dark';
const DARK_MEDIA_QUERY = '(prefers-color-scheme: dark)';

/**
 * Bootstrap 5.3 ships its own dark palette behind an attribute on an ancestor. It paints tooltips,
 * popovers and list groups, which the Material theme never reaches, so the theme has to set it or
 * those surfaces stay white on a dark page.
 */
const VENDOR_THEME_ATTRIBUTES = ['data-bs-theme'] as const;

/**
 * Deliberately outside `LocalStorageService`'s namespace: that one is cleared wholesale when a
 * session expires, and which appearance someone wants belongs to the device, not the session.
 * `index.html` reads the same key before the app boots, so the two must stay in step.
 */
const STORAGE_KEY = 'fb-theme';

@Service()
export class ThemeService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);
  private readonly overlayContainer = inject(OverlayContainer);
  private readonly destroyRef = inject(DestroyRef);

  private readonly preference = signal<ThemePreference>('light');

  readonly isDarkMode = computed(() => this.preference() === 'dark');

  init(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.applyPreference(this.storedPreference() ?? this.systemPreference());
    this.followSystemPreference();
    this.followOtherTabs();
  }

  toggle(): void {
    this.setPreference(this.isDarkMode() ? 'light' : 'dark');
  }

  setPreference(preference: ThemePreference): void {
    this.applyPreference(preference);
    this.storePreference(preference);
  }

  private applyPreference(preference: ThemePreference): void {
    this.preference.set(preference);

    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const isDark = preference === 'dark';
    const root = this.document.documentElement;

    root.classList.toggle(DARK_CLASS, isDark);
    VENDOR_THEME_ATTRIBUTES.forEach(attribute => root.setAttribute(attribute, preference));

    // The overlay container is a sibling of the app root, so a theme scoped to `<html>` reaches it
    // anyway -- but dialogs, menus and tooltips are the surfaces people notice first when it does
    // not, and the CDK moves the container between the body and a fullscreen element.
    this.overlayContainer.getContainerElement().classList.toggle(DARK_CLASS, isDark);
  }

  /**
   * Tracks the operating system until someone makes a choice of their own, after which the stored
   * preference is the answer and the system setting is ignored.
   */
  private followSystemPreference(): void {
    const query = this.document.defaultView?.matchMedia?.(DARK_MEDIA_QUERY);

    if (!query) {
      return;
    }

    const onChange = (event: MediaQueryListEvent) => {
      if (this.storedPreference() === null) {
        this.applyPreference(event.matches ? 'dark' : 'light');
      }
    };

    query.addEventListener('change', onChange);
    this.destroyRef.onDestroy(() => query.removeEventListener('change', onChange));
  }

  /** A choice made in another tab of the app reaches this one as a `storage` event. */
  private followOtherTabs(): void {
    const view = this.document.defaultView;

    if (!view) {
      return;
    }

    const onStorage = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY) {
        this.applyPreference(this.storedPreference() ?? this.systemPreference());
      }
    };

    view.addEventListener('storage', onStorage);
    this.destroyRef.onDestroy(() => view.removeEventListener('storage', onStorage));
  }

  private systemPreference(): ThemePreference {
    return this.document.defaultView?.matchMedia?.(DARK_MEDIA_QUERY).matches ? 'dark' : 'light';
  }

  private storedPreference(): ThemePreference | null {
    // Private browsing and blocked site data both throw on access rather than returning nothing.
    try {
      const stored = this.document.defaultView?.localStorage.getItem(STORAGE_KEY);

      return stored === 'dark' || stored === 'light' ? stored : null;
    } catch {
      return null;
    }
  }

  private storePreference(preference: ThemePreference): void {
    try {
      this.document.defaultView?.localStorage.setItem(STORAGE_KEY, preference);
    } catch {
      // A preference that cannot be stored still applies for this page.
    }
  }
}
